import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead } from "../components/Motion";
import { BeforeAfter } from "../components/BeforeAfter";
import { WalkthroughForm } from "../components/Forms";
import { ClientStrip, FaqBlock, FinalCta, FinishTiers, WarrantyModule } from "../components/Sections";
import { SERVICE_PAGES, QUOTE_DRIVERS } from "../data/services";

export const Highlights = ({ items, title = "What's included" }) => (
  <section className="bg-paper py-20 sm:py-28" data-testid="service-highlights">
    <div className="container-x grid gap-10 lg:grid-cols-12">
      <div className="lg:col-span-4"><SectionHead eyebrow="Scope" title={title} /></div>
      <ul className="grid gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:col-span-8">
        {items.map((h, i) => (
          <Reveal as="li" key={h} delay={i * 0.04} className="flex items-center gap-4 bg-white p-6">
            <Check className="h-5 w-5 shrink-0 text-flame-600" /><span className="font-display text-lg font-bold text-navy">{h}</span>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export const ProcessExplainer = ({ steps, image, alt }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="process-explainer">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <SectionHead eyebrow="The process" title="Exactly how we do it." intro="Same standard on every job — the reason our clients don't have to check on us." />
          <div className="mt-10 aspect-[4/3] overflow-hidden rounded-md"><img src={image} alt={alt} className="h-full w-full object-cover" loading="lazy" /></div>
        </div>
      </div>
      <ol className="lg:col-span-7">
        {steps.map((s, i) => (
          <Reveal as="li" key={s.t} className="flex gap-6 border-t border-slate-200 py-10 first:border-t-0 first:pt-0">
            <span className="font-display text-5xl font-extrabold text-flame">0{i + 1}</span>
            <div>
              <h3 className="text-2xl font-extrabold text-navy">{s.t}</h3>
              <p className="mt-3 max-w-lg leading-relaxed text-slate-600">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export const QuoteDrivers = () => (
  <section className="bg-paper py-20 sm:py-28" data-testid="quote-drivers-section">
    <div className="container-x">
      <SectionHead eyebrow="What shapes your quote" title="No price lists. Just a transparent scope." intro="Every commercial space is different, so we quote from a site walkthrough — never a guess. These are the line items that drive your written quote, each spelled out so you can compare like for like." />
      <div className="mt-12 grid gap-px overflow-hidden rounded-md bg-slate-200 md:grid-cols-2 lg:grid-cols-3">
        {QUOTE_DRIVERS.map((d, i) => (
          <Reveal key={d.title} delay={i * 0.05} className="bg-white p-8">
            <p className="font-mono text-xs text-flame-600">0{i + 1}</p>
            <h3 className="mt-4 text-xl font-bold text-navy">{d.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.line}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const WalkthroughSection = ({ vertical }) => (
  <section id="walkthrough" className="scroll-mt-24 bg-navy py-20 sm:py-28" data-testid="walkthrough-section">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHead dark eyebrow="Request a site walkthrough" title="A project lead will connect with you within 24 hours." intro="Tell us about the space. We'll book a walkthrough, map the phases and send a written, itemized quote." />
      </div>
      <Reveal className="lg:col-span-7"><WalkthroughForm key={vertical} vertical={vertical} /></Reveal>
    </div>
  </section>
);

const VERTICAL_LABEL = { offices: "Offices", hospitality: "Hospitality", "high-rise": "High-Rise & Multi-Unit" };

export default function ServicePage({ pageKey }) {
  const params = useParams();
  const key = pageKey || params.key;
  const pg = SERVICE_PAGES[key];
  if (!pg) return <Navigate to="/" replace />;
  const res = pg.track === "residential";
  const hub = res ? { label: "Residential", to: "/residential" } : { label: "Commercial", to: "/commercial" };
  return (
    <>
      <Seo title={pg.seoTitle} description={pg.meta} />
      <PageHero eyebrow={pg.eyebrow.split(" · ")[1]} crumbs={[hub]} lines={pg.h1} intro={pg.intro} image={pg.image} alt={`${pg.eyebrow} painting by Amora Painting`} cta={res}>
        {!res && (
          <Reveal delay={0.45} className="mt-9 flex flex-wrap gap-3">
            <a href="#walkthrough" className="btn-primary" data-testid="page-hero-walkthrough-btn">Request a Site Walkthrough</a>
            <Link to="/book" className="btn-ghost-dark" data-testid="page-hero-book-btn">Book a free assessment</Link>
          </Reveal>
        )}
      </PageHero>
      <Highlights items={pg.highlights} />
      <ProcessExplainer steps={pg.process} image={pg.wide} alt={`${pg.eyebrow} project detail`} />
      <section className="bg-paper py-20 sm:py-28" data-testid="service-slider-section">
        <div className="container-x grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="Before / After" title="Drag the handle." intro="Real outcomes, captioned by neighbourhood, scope and duration." />
            <Link to="/projects" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-navy" data-testid="service-projects-link"><span className="border-b-2 border-flame">More projects</span><ArrowUpRight className="h-4 w-4" /></Link>
          </div>
          <Reveal className="lg:col-span-8"><BeforeAfter {...pg.project} testId="service-before-after-slider" /></Reveal>
        </div>
      </section>
      {res ? <FinishTiers /> : <><QuoteDrivers /><ClientStrip /></>}
      <WarrantyModule />
      <FaqBlock faqs={pg.faqs} />
      {res ? <FinalCta /> : <WalkthroughSection vertical={VERTICAL_LABEL[pg.vertical]} />}
    </>
  );
}
